import React, { forwardRef, useEffect } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import DetailsText from 'pages/approval-center/components/DetailsText'
import BodyText from 'pages/approval-center/components/BodyText'
import ExpenseListPayable from 'pages/approval-center/components/ExpenseListPayable'
import { ErrorMessage, Field, FieldArray, Form, Formik } from 'formik'
import * as yup from 'yup'
import AdxErrorMessage from 'components/custom/AdxErrorMessage'
import ClaimProgressStepper from 'module/claim/components/ClaimProgressStepper'
import { useQuery } from 'react-query'
import {
  getSingleApprovalReport,
  getSingleReimbursementReport,
} from 'services/api/modules/claim/approval-center/report'
import { CircularProgress } from '@mui/material'
import { useLocation } from 'react-router-dom'
import { renderCountBadge } from 'components/custom/AdxLabelRenderer'

const ClaimRequestFormEffects = ({ values, isValid, setAmount, setSubmitDisable }) => {
  useEffect(() => {
    const totalPayable = (values.expenseArray || []).reduce((sum, list) => {
      const amount = parseFloat(list.amount_payable)
      return sum + (isNaN(amount) ? 0 : amount)
    }, 0)

    setAmount(`RM ${totalPayable.toFixed(2)}`)

    const hasExpenses = values.expenseArray?.length > 0
    const hasInvalidPayable = values.expenseArray?.some((item) => {
      const payable = parseFloat(item.amount_payable)

      return isNaN(payable) || payable <= 0
    })

    setSubmitDisable(!(hasExpenses && isValid && !hasInvalidPayable))
  }, [values, isValid, setAmount, setSubmitDisable])

  return null
}

const ClaimRequest = forwardRef(
  ({ reportData, editable, setAmount, setSubmitDisable, tab }, ref) => {
    const { id } = reportData
    const { pathname } = useLocation()
    const isApproval = pathname?.includes('approval-center')

    const {
      data: reportDataApi,
      isLoading,
      isSuccess,
      isError,
    } = useQuery(
      [isApproval ? 'singleApprovalReport' : 'singleReimbursementReport', id],
      async () => {
        const { data } = isApproval
          ? await getSingleApprovalReport(id)
          : await getSingleReimbursementReport(id)
        return data
      }
    )

    const { staff_name, title, claim_report_type, description, start_date, end_date, expenses } =
      isSuccess && reportDataApi

    const { reimbursementTab, respondedTab } = tab

    const validationSchema = yup.object().shape({
      expenseArray: yup.array().of(
        yup.object().shape({
          amount_payable: yup
            .number()
            .typeError('Invalid amount')
            .positive('Invalid amount')
            .min(0.01, 'Invalid amount')
            .required('Amount is required'),
        })
      ),
    })

    return (
      <>
        {isLoading && (
          <Box height={200} display="flex" justifyContent="center" alignItems="center">
            <CircularProgress sx={{ color: 'alternate' }} />
          </Box>
        )}

        {isSuccess && (
          <Box sx={{ mb: 4 }}>
            <DetailsText title="Title" text={title} />
            <DetailsText title="Type" text={claim_report_type} />
            <DetailsText title="Claim date" text={`${start_date} - ${end_date}`} />
            <DetailsText title="Requester" text={staff_name} />
            <BodyText title="Claim purpose" text={description} />

            <BodyText
              title={
                <Box display="flex" alignItems="center" columnGap={1}>
                  <Typography variant="body1" color="light-gray-text.main">
                    Expenses
                  </Typography>
                  {renderCountBadge(expenses?.length)}
                </Box>
              }
            >
              <Formik
                enableReinitialize
                innerRef={ref}
                validateOnMount
                initialValues={{
                  expenseArray: expenses
                    ? expenses.map((item) => ({
                      id: item.id,
                      category: item?.expense_category?.name,
                      date: item.date,
                      merchant: item.merchant,
                      description: item.description,
                      amount: Number(
                        reimbursementTab || respondedTab ? item.amount_payable : item.amount
                      ).toFixed(2),
                      amount_payable: Number(
                        item.amount_payable === 0 ? item.amount : item.amount_payable
                      ).toFixed(2),
                      proof_link: item.proof_link,
                      file: {
                        name: item?.attachment,
                        url: item?.attachment_url,
                      },
                    }))
                    : [],
                }}
                validationSchema={validationSchema}
              >
                {({ errors, values, handleChange, setFieldValue, isValid }) => (
                  <Form>
                    <ClaimRequestFormEffects
                      values={values}
                      isValid={isValid}
                      setAmount={setAmount}
                      setSubmitDisable={setSubmitDisable}
                    />

                    <FieldArray
                      name="expenseArray"
                      render={() =>
                        values.expenseArray.map((item, index) => (
                          <Field
                            key={item.id ?? index}
                            name={`expenseArray.${index}.amount_payable`}
                            as={ExpenseListPayable}
                            onChange={handleChange}
                            onBlur={(e) => {
                              const rawValue = parseFloat(e.target.value)

                              if (isNaN(rawValue) || rawValue <= 0) {
                                setFieldValue(`expenseArray.${index}.amount_payable`, '')
                                return
                              }

                              setFieldValue(
                                `expenseArray.${index}.amount_payable`,
                                rawValue.toFixed(2)
                              )
                            }}
                            editable={editable}
                            id={item.id}
                            reportId={id}
                            category={item.category}
                            date={item.date}
                            merchant={item.merchant}
                            description={item.description}
                            amount={item.amount}
                            proofLink={item.proof_link}
                            file={item?.file}
                            value={values.expenseArray[index].amount_payable}
                            errorMessage={
                              <ErrorMessage
                                name={`expenseArray.${index}.amount_payable`}
                                component={AdxErrorMessage}
                              />
                            }
                            isErrorShown={!!errors?.expenseArray?.[index]?.amount_payable}
                          />
                        ))
                      }
                    />
                  </Form>
                )}
              </Formik>
            </BodyText>

            <ClaimProgressStepper reportData={reportDataApi} />
          </Box>
        )}

        {isError && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Typography variant="body1" color="light-gray-text.main">
              This feature is currently unavailable for self-approved claim requests.
            </Typography>
          </Box>
        )}
      </>
    )
  }
)

export default ClaimRequest
